import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { compact, xor } from 'lodash-es';
import { DataTable } from '@ui';
import { SearchInput } from '@ds';
import { HubsDataDictionary } from '@lp/features/Scheduler/types';
import { CSResource } from '@/api/apis/resources/schedule';
import { getFullName } from '@/utils/helpers';
import { getRenderClinicalSpecialistTableCell, CLINICAL_SPECIALIST_COLUMNS } from './config';

type InsertSpecialistToHubListProps = {
  hubId: string;
  resourceList: CSResource[];
  hubsDictionary: HubsDataDictionary;
  insertedIds: string[];
  selectedIds: string[];
  onChange: (userIds: string[]) => void;
};

const noopFn = () => {};

export const InsertSpecialistToHubList = ({
  hubId,
  resourceList,
  hubsDictionary,
  insertedIds,
  selectedIds,
  onChange,
}: InsertSpecialistToHubListProps) => {
  const [searchValue, setSearchValue] = useState('');

  const dataList = useMemo(() => {
    const list = compact(resourceList).map((r) => {
      return { ...r, isSelected: selectedIds.includes(r.id), hubName: hubsDictionary[r.hubId]?.name };
    });

    return searchValue
      ? list.filter((r) => {
          const fullName = getFullName(r.firstName, r.lastName);
          const hasName = fullName.toLowerCase().includes(searchValue.toLowerCase());
          const hasHubName = r.hubName?.toLowerCase().includes(searchValue.toLowerCase());

          return hasName || hasHubName;
        })
      : list;
  }, [searchValue, resourceList, hubsDictionary, selectedIds]);

  const handleSelect = useCallback((userId: string) => onChange(xor(selectedIds, [userId])), [selectedIds, onChange]);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const renderClinicalSpecialistTableCell = useMemo(() => {
    return getRenderClinicalSpecialistTableCell(hubId, insertedIds, handleSelect);
  }, [hubId, insertedIds, handleSelect]);

  return (
    <>
      <SearchInput
        placeholder="Search"
        value={searchValue}
        onChange={onSearchChange}
        className="mb-6 sticky top-0 z-10"
      />
      <DataTable
        type="default"
        label="Clinical Specialists"
        hidePadding
        hidePagination
        columns={CLINICAL_SPECIALIST_COLUMNS}
        renderCell={renderClinicalSpecialistTableCell}
        data={dataList || []}
        // FIXME: Redundant props
        isLoading={false}
        isFetching={false}
        totalItems={dataList.length}
        itemsPerPage={dataList.length}
        totalPages={1}
        page={1}
        onPageChange={noopFn}
        onItemsPerPageChange={noopFn}
        onSortByChange={noopFn}
      />
    </>
  );
};
